import React, { useState } from "react";
import { css } from "@emotion/react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, ConfirmModal, Title } from "@src/components";
import { INVALID_VALUE } from "@src/constants/InvalidValue";
import {
    useCommunityDetail,
    useDeleteCommunityComment,
    useDeleteCommunityPost,
} from "./service";
import {
    BID_TYPE_KOR,
    CommunityCommentDto,
    CommunityPostItemDto,
} from "./service/types";

type DeleteTarget =
    | { type: "post"; postNo: number }
    | { type: "comment"; commentNo: number };

const renderPostedItem = (item: CommunityPostItemDto): string => {
    const bidType = BID_TYPE_KOR[item.bidType] ?? "";
    const label = item.subId ?? item.itemId;
    return bidType ? `[${bidType}] ${label}` : label;
};

const CommunityDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const postNo = Number(id);

    const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

    const { data } = useCommunityDetail(postNo);
    const deletePost = useDeleteCommunityPost();
    const deleteComment = useDeleteCommunityComment();

    const itemList: CommunityPostItemDto[] = data?.itemList ?? [];
    const commentList: CommunityCommentDto[] = data?.commentList ?? [];

    const handleDelete = () => {
        if (!deleteTarget) {
            return;
        }
        if (deleteTarget.type === "post") {
            deletePost.mutate(deleteTarget.postNo, {
                onSuccess: () => {
                    setDeleteTarget(null);
                    navigate("/community/list");
                },
                onError: () => {
                    alert("글 삭제에 실패했습니다.");
                    setDeleteTarget(null);
                },
            });
            return;
        }
        deleteComment.mutate(deleteTarget.commentNo, {
            onSuccess: () => {
                setDeleteTarget(null);
            },
            onError: () => {
                alert("댓글 삭제에 실패했습니다.");
                setDeleteTarget(null);
            },
        });
    };

    return (
        <div css={detailStyle}>
            <Title title="커뮤니티 글 상세 조회" />

            <section css={sectionStyle}>
                <div css={sectionHeaderStyle}>
                    <Title title="글 관련 내용" size="small" />
                    <Button
                        color="purple"
                        size="small"
                        label="삭제"
                        handleClick={() =>
                            setDeleteTarget({ type: "post", postNo })
                        }
                    />
                </div>

                <div css={rowStyle}>
                    <span className="label">유저 이름</span>
                    <span className="value">{data?.writer ?? INVALID_VALUE}</span>
                </div>
                <div css={rowStyle}>
                    <span className="label">글 제목</span>
                    <span className="value">{data?.title ?? INVALID_VALUE}</span>
                </div>
                <div css={rowStyle}>
                    <span className="label">글 내용</span>
                    <span className="value content">
                        {data?.content ?? INVALID_VALUE}
                    </span>
                </div>
                <div css={rowStyle}>
                    <span className="label">올린 물건</span>
                    <span className="value">
                        {itemList.length > 0
                            ? itemList.map(renderPostedItem).join(", ")
                            : INVALID_VALUE}
                    </span>
                </div>
            </section>

            {commentList.map((comment, index) => (
                <section key={comment.commentNo} css={sectionStyle}>
                    <div css={sectionHeaderStyle}>
                        <Title title={`댓글 ${index + 1}`} size="small" />
                        <Button
                            color="purple"
                            size="small"
                            label="삭제"
                            handleClick={() =>
                                setDeleteTarget({
                                    type: "comment",
                                    commentNo: comment.commentNo,
                                })
                            }
                        />
                    </div>

                    <div css={rowStyle}>
                        <span className="label">작성자</span>
                        <span className="value">
                            {comment.writer ?? INVALID_VALUE}
                        </span>
                    </div>
                    <div css={rowStyle}>
                        <span className="label">댓글 내용</span>
                        <span className="value content">
                            {comment.content ?? INVALID_VALUE}
                        </span>
                    </div>
                    <div css={rowStyle}>
                        <span className="label">작성일</span>
                        <span className="value">
                            {comment.createdAt?.slice(0, 10) ?? INVALID_VALUE}
                        </span>
                    </div>
                </section>
            ))}

            <ConfirmModal
                isModalShow={Boolean(deleteTarget)}
                buttonText="삭제"
                handleCancel={() => setDeleteTarget(null)}
                handleOk={handleDelete}
            >
                정말 삭제하시겠습니까?
            </ConfirmModal>
        </div>
    );
};

export default CommunityDetail;

const detailStyle = css`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 40px;
    gap: 24px;
`;

const sectionStyle = css`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    background-color: #f4f5f7;
    border-radius: 8px;
`;

const sectionHeaderStyle = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const rowStyle = css`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    font-size: 15px;
    color: #222222;

    & > .label {
        width: 96px;
        flex-shrink: 0;
        color: #666666;
    }

    & > .value {
        flex: 1;
        word-break: break-word;
    }

    & > .value.content {
        white-space: pre-wrap;
        line-height: 1.5;
    }
`;
